import { dataURLtoFile } from '@/shared/utils/mediaUtils';
import { setAlertModal, resetAlertModal } from '@/components/AlertModalRoot/alertModalService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CameraIcon } from '@/assets/images';
import { hidePopup } from '@/components/PopupContainer/popupService';

const INITIAL_STATE = {
   error: null,
};

const ERROR_TYPES = {
   PERMISSION: 'permission',
   CAMERA: 'camera',
};

/**
 * Custom hook for managing WebCamera state and operations
 * @param {Function} onCapture - Callback when photo is captured (file, dataUri)
 * @param {Function} onClose - Callback when camera is closed
 * @returns {Object} Camera state and handlers
 */
export const useWebCamera = (onCapture, onClose) => {
   const [state, setState] = useState(INITIAL_STATE);
   const { error } = state;
   const streamRef = useRef(null);

   const handleCameraStart = useCallback(stream => {
      if (streamRef.current && streamRef.current !== stream) {
         streamRef.current.getTracks().forEach(track => track.stop());
      }
      streamRef.current = stream;
   }, []);

   const stopCameraStream = useCallback(() => {
      if (streamRef.current) {
         streamRef.current.getTracks().forEach(track => track.stop());
         streamRef.current = null;
      }
   }, []);

   const handleTakePhoto = useCallback(
      dataUri => {
         stopCameraStream();
         if (!dataUri || dataUri.startsWith('data:,')) return;
         const file = dataURLtoFile(dataUri, 'captured-image.png');
         onCapture({ file, dataUri });
      },
      [stopCameraStream, onCapture],
   );

   const handleCameraError = useCallback(
      error => {
         stopCameraStream();
         const errorType = error?.name === 'NotAllowedError' ? ERROR_TYPES.PERMISSION : ERROR_TYPES.CAMERA;
         setState({ error: errorType });
      },
      [stopCameraStream],
   );

   const handleClose = useCallback(() => {
      stopCameraStream();
      onClose();
   }, [onClose, stopCameraStream]);

   // Cleanup on unmount
   useEffect(() => {
      return () => stopCameraStream();
   }, [stopCameraStream]);

   const getErrorConfig = useCallback(errorType => {
      if (!errorType) return null;
      return {
         title: errorType === ERROR_TYPES.PERMISSION ? 'Permission Denied' : 'Camera Not Found',
         message:
            errorType === ERROR_TYPES.PERMISSION
               ? 'Please allow camera access to take a photo.'
               : 'Your camera could not be accessed.',
      };
   }, []);

   useEffect(() => {
      if (error) {
         hidePopup('avatar_camera_popup');
         const config = getErrorConfig(error);
         const modalId = 'web-camera-error-modal';

         setAlertModal({
            id: modalId,
            visible: true,
            title: config.title,
            icon: <CameraIcon />,
            optionalButton: 'Okay',
            description: config.message,
            optionalAction: () => {
               resetAlertModal(modalId);
               onClose?.();
            },
         });
      }
   }, [error, onClose, getErrorConfig]);

   return {
      state: { error },
      handlers: {
         handleTakePhoto,
         handleCameraError,
         handleClose,
         handleCameraStart,
         handleCameraStop: stopCameraStream,
      },
      utils: {
         getErrorConfig,
      },
   };
};
